/**
 * Pending Downgrade Banner
 * 
 * Banner displayed at the top of billing page when a downgrade is pending
 * Shows effective date and allows cancellation
 * 
 * @package @ideoz/ui
 */

import React from 'react';
import { Button } from '../atoms/button';
import { cn } from '@ideoz/ui';

export interface PendingDowngradeBannerProps {
  isVisible: boolean;
  effectiveDate: string;
  onCancelDowngrade: () => void;
  isLoading?: boolean;
  onClose?: () => void;
  className?: string;
  testId?: string;
}

export default function PendingDowngradeBanner({
  isVisible,
  effectiveDate,
  onCancelDowngrade,
  isLoading = false,
  onClose,
  className,
  testId = 'pending-downgrade-banner',
}: PendingDowngradeBannerProps): JSX.Element | null {
  if (!isVisible) return null;

  return (
    <div
      data-testid={testId}
      className={cn(
        'bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6',
        className
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-700 mb-1">
            Your downgrade will take effect on {effectiveDate}.
          </p>
          <p className="text-sm text-gray-600">
            You'll continue to have access to Smart features until then.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancelDowngrade}
            disabled={isLoading}
            className="rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-semibold h-8 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`${testId}-cancel-button`}
          >
            {isLoading ? 'Processing...' : 'Cancel downgrade'}
          </Button>
        </div>
      </div>
    </div>
  );
}
